import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  buildRestaurantDetail,
  isPickRow,
  isProfileRow,
  type RestaurantDetail,
} from '../lib/restaurantDetail';

interface UseRestaurantByPlaceIdResult {
  detail: RestaurantDetail | null;
  isLoaded: boolean;
  error: string | null;
}

// Resolves /place/:placeId to a single restaurant + the recommenders who
// picked it. Unlike useRestaurantBySlug, the place_id is the canonical
// identity, so no name matching is needed — we fetch every pick + profile
// and roll up the one place directly.
export function useRestaurantByPlaceId(
  placeId: string | undefined
): UseRestaurantByPlaceIdResult {
  const [detail, setDetail] = useState<RestaurantDetail | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!placeId) {
      setDetail(null);
      setIsLoaded(true);
      return;
    }

    let mounted = true;
    setIsLoaded(false);

    (async () => {
      const [picksRes, profilesRes] = await Promise.all([
        supabase
          .from('user_picks')
          .select('user_id, name, place_id, cuisine, location, position'),
        supabase
          .from('profiles')
          .select('user_id, slug, display_name'),
      ]);
      if (!mounted) return;

      if (picksRes.error) {
        setError(picksRes.error.message);
        setIsLoaded(true);
        return;
      }
      if (profilesRes.error) {
        setError(profilesRes.error.message);
        setIsLoaded(true);
        return;
      }

      const allPicks = Array.isArray(picksRes.data)
        ? picksRes.data.filter(isPickRow)
        : [];
      const allProfiles = Array.isArray(profilesRes.data)
        ? profilesRes.data.filter(isProfileRow)
        : [];

      setDetail(buildRestaurantDetail(allPicks, allProfiles, placeId));
      setIsLoaded(true);
      setError(null);
    })();

    return () => {
      mounted = false;
    };
  }, [placeId]);

  return { detail, isLoaded, error };
}
